import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import React from "react";
import { useToast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import { BusinessUnit } from "@/types/system-setting/business-unit";
import { useDeleteBusinessUnitMutation } from "@/services/businessUnitApi";
import warning from "@/assets/warning.svg";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

export function DataTableRowActions({
    data,
    refetch,
    handleEdit,
    access,
}: {
    data: BusinessUnit;
    refetch: () => void;
    handleEdit: (payload: BusinessUnit) => void;
    access: string;
}) {
    const [isDelete, setIsDelete] = React.useState(false);
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
    const [deleteBusinessUnit, { isLoading: deleteLoading }] = useDeleteBusinessUnitMutation();
    const { toast } = useToast();

    const handleDelete = async () => {
        try {
            await deleteBusinessUnit({ id: data.id, org_id: orgId }).unwrap();
            refetch();
            toast({ variant: "success", title: "Business Unit Deleted Successfully" });
        } catch (error) {
            const typedError = error as ErrorType;
            toast({
                variant: "destructive",
                title: "Error",
                description: typedError?.status === 400
                    ? "Business Unit is assigned to active users and cannot be deleted."
                    : typedError?.data?.detail ?? "Something went wrong.",
            });
        }
    };

    return (
    <>
        <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">Open menu</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(data);
                }}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                </DropdownMenuItem>
                {access === "full_access" && (
                    <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        setIsDelete(true);
                    }}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>

        {isDelete && (
            <AlertDialog open={isDelete} onOpenChange={setIsDelete}>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                        <div className="flex flex-col items-center gap-4">
                            <img src={warning} alt="warning" className="w-18 h-18" />
                            <AlertDialogTitle className="text-xl font-semibold text-center">
                                Are you sure you want to delete this Business Unit?
                            </AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-center">
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsDelete(false);
                            }}
                            className="w-full border border-primary font-semibold"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete();
                            }}
                            className="w-full bg-primary text-black font-semibold"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        )}
    </>
);
}
